import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Phone, Mail, MapPin, Globe, Clock, Instagram, Facebook } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useSettings } from '../../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { refreshSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        setForm(res.data);
      } catch (err) {
        toast.error('Erro ao carregar definições');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/settings`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Definições guardadas com sucesso');
      refreshSettings();
    } catch {
      toast.error('Erro ao guardar definições');
    } finally {
      setSaving(false);
    }
  };

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (loading || !form) return <AdminLayout><div className="text-center py-20"><p className="text-zinc-400">A carregar...</p></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-3xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-medium text-white" data-testid="settings-title">Definições</h1>
            <p className="text-zinc-500">Informações de contacto e redes sociais</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-settings-btn">
            <Save size={18} className="mr-2" />
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </div>

        {/* Phone & Email */}
        <div className="bg-[#12141D] border border-zinc-800 p-6 mb-6">
          <h3 className="text-white font-medium mb-5 flex items-center gap-2"><Phone size={18} className="text-red-500" /> Contacto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">Telemóvel (visível)</label>
              <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="919 714 805" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-phone" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">Telemóvel (internacional)</label>
              <Input value={form.phone_intl} onChange={e => update('phone_intl', e.target.value)} placeholder="+351919714805" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-phone-intl" />
              <p className="text-zinc-600 text-xs mt-1">Usado para WhatsApp e chamadas</p>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block flex items-center gap-1.5"><Mail size={14} /> Email</label>
              <Input value={form.email} onChange={e => update('email', e.target.value)} placeholder="info@icardetail.pt" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-email" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block flex items-center gap-1.5"><Clock size={14} /> Horário</label>
              <Input value={form.schedule} onChange={e => update('schedule', e.target.value)} placeholder="Seg-Sex: 9h-19h" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-schedule" />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-[#12141D] border border-zinc-800 p-6 mb-6">
          <h3 className="text-white font-medium mb-5 flex items-center gap-2"><MapPin size={18} className="text-red-500" /> Morada</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">Linha 1</label>
              <Input value={form.address_line1} onChange={e => update('address_line1', e.target.value)} className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-addr1" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">Linha 2 (Código Postal)</label>
              <Input value={form.address_line2} onChange={e => update('address_line2', e.target.value)} className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-addr2" />
            </div>
            <div className="md:col-span-2">
              <label className="text-zinc-400 text-sm mb-1.5 block">Morada completa</label>
              <Input value={form.address_full} onChange={e => update('address_full', e.target.value)} className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-addr-full" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">Link Google Maps</label>
              <Input value={form.maps_url} onChange={e => update('maps_url', e.target.value)} placeholder="https://maps.app.goo.gl/..." className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-maps-url" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">Embed Google Maps</label>
              <Input value={form.maps_embed} onChange={e => update('maps_embed', e.target.value)} placeholder="https://maps.google.com/maps?..." className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-maps-embed" />
              <p className="text-zinc-600 text-xs mt-1">URL de embed para o mapa na homepage</p>
            </div>
          </div>
        </div>

        {/* Social & Website */}
        <div className="bg-[#12141D] border border-zinc-800 p-6">
          <h3 className="text-white font-medium mb-5 flex items-center gap-2"><Globe size={18} className="text-red-500" /> Website e Redes Sociais</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block flex items-center gap-1.5"><Globe size={14} /> Website</label>
              <Input value={form.website} onChange={e => update('website', e.target.value)} placeholder="icardetail.pt" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-website" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block flex items-center gap-1.5"><Instagram size={14} /> Instagram</label>
              <Input value={form.instagram_url} onChange={e => update('instagram_url', e.target.value)} placeholder="https://instagram.com/icardetail" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-instagram" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block flex items-center gap-1.5"><Facebook size={14} /> Facebook</label>
              <Input value={form.facebook_url} onChange={e => update('facebook_url', e.target.value)} placeholder="https://facebook.com/icardetail" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="settings-facebook" />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
